import React, { useCallback, useEffect, useState } from 'react'
import { Comment } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'

import { Box, Button, CircularProgress, Collapse, Drawer, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { Icons, boundaryValue, convertToCDNLink, exportAssignmentReport, extractAssignmentFileDetailsFromMinioLocationOrKey, formatDate, isIOS } from '../../utils/utilities'
import AssignmentSubmissionDownloadDialog from '../../components/presenter/AssignmentSubmissionDownloadDialog'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import DocumentViewer from '../../components/common/DocumentViewer'
import BreadcrumbList from '../../components/common/BreadcrumbList'
import { useAssignmentStore } from '../../store/assignment.store'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import TableHeader from '../../components/common/TableHeader'
import noSubmissionImg from '../../assets/nosubmissions.svg'
import api from '../../service/api'

const columns = [
    { name: 'Student', width: 260, padding: "10px 20px 10px 40px" },
    { name: 'AUID', width: 160 },
    { name: 'Submitted on', minWidth: 100 },
    // { name: 'Attachment', width: 200, align: "center" },
    { name: 'Score', width: 120, align: "center" },
    // { name: 'Remark', width: 90, align: "center" },
    { name: '', width: 100, padding: "10px 40px 10px 20px", align: "right" },
]

const ViewerSubmission = ( { setSelectedSubmissionForAction, submission, viewSubmission, selected } ) => {

    const [collapseState, setCollapseState] = useState( false )
    const [downloading, setDownloading] = useState( false )

    const AssignmentStore = useAssignmentStore()

    const { palette, border } = useTheme()


    const downloadSubmission = async ( value ) => {
        setDownloading( value )
        const { groups } = extractAssignmentFileDetailsFromMinioLocationOrKey( value )
        console.log( groups )
        await AssignmentStore.downloadAssignment( value, groups.fullFileName )
        setDownloading( null )
    }

    const handleClickOnSubmission = ( item, index ) => {
        const submissionFileId = [+submission.assignment_submission_id, index, +submission.id - 1]
        setSelectedSubmissionForAction( submission )
        console.log( "Selected Score: ", submission )

        if ( item.endsWith( "pdf" ) || !isIOS() ) {
            viewSubmission( item, submissionFileId )
        }
        else toast( `View option not supported for this format on this device` )
    }

    return (
        <Box borderRight={border[1]} key={submission.assignment_submission_id}>
            <Box sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} onClick={() => setCollapseState( p => !p )} padding="10px 20px" display="flex" gap="20px" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography color={+selected[0] === +submission.assignment_submission_id && palette.primary.main} variant='subtitle2'>{submission.student_name}</Typography>
                    <Typography color={+selected[0] === +submission.assignment_submission_id && palette.primary.main} variant='body2' fontSize="12px">{submission.assignment_submitted_by}</Typography>
                </Box>
                {Icons.default.KeyboardArrowDownIcon}
            </Box>
            <Collapse in={collapseState || +submission.assignment_submission_id === +selected[0]}>
                <Box borderTop={border[1]}>
                    {submission?.assignment_submission_file_paths?.map( ( item, index ) => {
                        const submissionSelected = +selected[0] === +submission.assignment_submission_id && +selected[1] === index
                        return (
                            <Box onClick={() => handleClickOnSubmission( item, index )} justifyContent="space-between" display="flex" alignItems="center" padding="5px 20px 5px 40px" borderBottom={border[1]} color={palette.secondary.main} gap="20px" key={item}>
                                <Typography color={submissionSelected ? palette.primary.main : "textSecondary"} fontSize="14px" maxWidth="80%" sx={{ wordBreak: "break-all", wordWrap: "break-word", fontWeight: selected ? "500" : "300", fontStyle: selected ? "italic" : "normal", cursor: "pointer", ":hover": { color: !submissionSelected && palette.common.font } }} display="flex" gap="10px" alignItems="center" >
                                    {index + 1}. {item.split( "." )[item.split( "." ).length - 2]?.replace( /%\d+/g, " " )}
                                </Typography>
                                <Box flexShrink={0}>
                                    <IconButton size='small' onClick={( e ) => { e.stopPropagation(); downloadSubmission( item ) }}>
                                        {downloading === item ? <CircularProgress size={14} /> : Icons.small.DownloadIcon}
                                    </IconButton>
                                </Box>
                            </Box>
                        )
                    } )}
                </Box>
            </Collapse>
        </Box>
    )
}

const EvaluateAssignment = observer( () => {
    let { assignment_id } = useParams()

    const AssignmentStore = useAssignmentStore()

    const [open, setOpen] = React.useState( false )
    const [loaded, setLoaded] = React.useState( false )
    const [assignedScore, setAssignedScore] = useState( 0 )
    const [deleting, setDeleting] = React.useState( false )
    const [previewFile, setPreviewFile] = React.useState( null )
    const [dialogType, setDialogType] = React.useState( 'download' ) // download || setmarks
    const [pageWidthAndScale, setPageWidthScaleFactor] = React.useState( 1 )
    const [loadingSubmission, setLoadingSubmission] = React.useState( false )
    const [selectedSubmission, setSelectedSubmission] = React.useState( null )
    const [previewingFileName, setPreviewingFileName] = React.useState( null )
    const [selectedForDeletion, setSelectedForDeletion] = React.useState( null )
    const [selectedSubmissionForAction, setSelectedSubmissionForAction] = React.useState( [] )

    const { palette, border, table } = useTheme()

    const handleClose = () => {
        setOpen( null )
    }

    const handlePdfPageResize = useCallback( ( delta, setToFit ) => {
        if ( setToFit )
            return setPageWidthScaleFactor( { scale: 1, width: window.innerWidth > 600 ? '' : window.innerWidth - 100 } )
        setPageWidthScaleFactor( prev => {
            const newScale = boundaryValue( 0.5, ( prev.scale || 1 ) + delta, 2 )
            return { scale: newScale, width: window.innerWidth > 600 ? '' : window.innerWidth - 100 }
        } )
    }, [] )

    const viewSubmission = async ( value, id ) => {
        setLoadingSubmission( true )
        try {
            const fileDetails = extractAssignmentFileDetailsFromMinioLocationOrKey( value ).groups
            const { data: { data } } = await api.downloadAssignment( { sourceUrl: decodeURIComponent( value ), preSignedUrl: true }, true )
            setPreviewingFileName( decodeURIComponent( `${fileDetails.fileName?.replace( /(\d+)?.?()/, "$2" )}.${fileDetails.fileExtension}` ) )

            const cdnLink = convertToCDNLink( data )
            setPreviewFile( cdnLink )
            console.log( cdnLink )

            setSelectedSubmission( id )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || `An error occurred while viewing the submission!` )
        } finally {
            setLoadingSubmission( false )
        }
    }

    const openDownloadDialog = ( e, value ) => {
        viewSubmission( value?.assignment_submission_file_paths?.at( 0 ), [+value.assignment_submission_id, 0, +value.id - 1] )
        setSelectedSubmissionForAction( value )
    }

    const deleteSubmission = async () => {
        setDeleting( true )
        await AssignmentStore.deleteAssignmentSubmission( selectedForDeletion )
        setSelectedForDeletion( null )
        setDeleting( false )
    }

    const openMarksAssignDialog = ( e, value ) => {
        setDialogType( 'setmarks' )
        setSelectedSubmissionForAction( value )
        setOpen( e.currentTarget )
    }

    const nextSubmission = () => {
        if ( selectedSubmission ) {
            const [subId, attIndex, subIndex] = selectedSubmission || []
            const currentSubmissions = AssignmentStore.getSubmission[subIndex]?.assignment_submission_file_paths
            console.log( subId, currentSubmissions?.length - 1, subIndex, attIndex )

            if ( currentSubmissions?.length - 1 === attIndex ) {
                if ( AssignmentStore.getSubmission?.length - 1 > subIndex + 1 ) {
                    const nextSub = AssignmentStore.getSubmission[subIndex + 1]
                    const nextFile = nextSub?.assignment_submission_file_paths[0]
                    viewSubmission( nextFile, [nextSub.assignment_submission_id, 0, nextSub.id - 1] )
                    setSelectedSubmissionForAction( nextSub )
                } else if ( AssignmentStore.getSubmission?.length === subIndex + 1 ) {
                    const nextSub = AssignmentStore.getSubmission[0]
                    const nextFile = nextSub?.assignment_submission_file_paths[0]
                    viewSubmission( nextFile, [nextSub.assignment_submission_id, 0, nextSub.id - 1] )
                    setSelectedSubmissionForAction( nextSub )
                }
            } else if ( currentSubmissions?.length - 1 > attIndex ) {
                const nextSub = AssignmentStore.getSubmission[subIndex]
                const nextFile = nextSub?.assignment_submission_file_paths[attIndex + 1]
                viewSubmission( nextFile, [nextSub.assignment_submission_id, attIndex + 1, nextSub.id - 1] )
                setSelectedSubmissionForAction( nextSub )
            }
        }
    }

    const prevSubmission = () => { }

    const submitScore = async ( e, event ) => {
        const value = e.target.value

        if ( value === selectedSubmissionForAction?.assignment_marks ) return
        if ( event === 'blur' || ( e.key === 'Enter' ) ) {
            let payload = { "assignment_submission_id": selectedSubmissionForAction.assignment_submission_id, "assignment_marks": value }
            if ( await AssignmentStore.setAssignmentScore( payload ) ) {
                setAssignedScore( value )
                setSelectedSubmissionForAction( prev => {
                    const newValue = { ...prev }
                    newValue.assignment_marks = value
                    return newValue
                } )
            } else
                setAssignedScore( selectedSubmissionForAction?.assignment_marks || 0 )
        }
    }

    const openReMarksAssignDialog = ( e, value ) => {
        setDialogType( 'setremarks' )
        setSelectedSubmissionForAction( value )
        setOpen( e.currentTarget )
    }

    useEffect( () => {
        setAssignedScore( selectedSubmissionForAction?.assignment_marks || 0 )
    }, [selectedSubmissionForAction] )

    useEffect( () => {
        const getData = async () => {
            await AssignmentStore.fetchAssignmentSubmission( assignment_id )
            setLoaded( true )
        }
        getData()
    }, [AssignmentStore, assignment_id] )


    return (
        <Box flexGrow={1} display="flex" flexDirection="column" overflow="auto" padding="20px">
            <Drawer anchor='bottom' slotProps={{ paper: { sx: { height: "calc(100% - 50px)" } } }} sx={{ zIndex: 9000000 }} open={Boolean( previewFile )} >
                <Box bgcolor={palette.cardBackground} flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                    <Box borderBottom={border[1]} flexWrap="wrap" display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Box display="flex" alignItems="center" gap="10px" >
                            <IconButton onClick={() => setPreviewFile( null )}>
                                {Icons.default.ArrowBack}
                            </IconButton>
                            <Box>
                                <Typography flexGrow={1} variant='h6' sx={{ wordBreak: "break-all", whiteSpace: "preserve-breaks" }} fontSize="14px">{previewingFileName}</Typography>
                                <Typography flexGrow={1} fontWeight="300" sx={{ wordBreak: "break-all", whiteSpace: "preserve-breaks", textTransform: "capitalize" }} fontSize="14px">By <u><em>{selectedSubmissionForAction.student_name?.toLowerCase()}</em></u></Typography>
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap="10px" >
                            {['pdf', 'jpeg', 'jpg'].includes( previewingFileName?.split( "." ).pop()?.toLowerCase() ) && <Box display="flex" alignItems="center" gap="5px">
                                <IconButton id="pdf-doc-zoom-out-btn" onClick={() => handlePdfPageResize( -0.1 )}>
                                    {Icons.small.ZoomOut}
                                </IconButton>
                                <Typography bgcolor={palette.contentBg} fontSize="12px" padding="5px" borderRadius="5px"  >{`${Math.round( ( pageWidthAndScale.scale || 1 ) * 100 )}%`}</Typography>
                                <IconButton id="pdf-doc-zoom-in-btn" onClick={() => handlePdfPageResize( 0.1 )}>
                                    {Icons.small.ZoomIn}
                                </IconButton>
                                <IconButton id="pdf-doc-fit-btn" onClick={() => handlePdfPageResize( 1, true )}>
                                    {Icons.small.CenterFocusStrongOutlined}
                                </IconButton>
                            </Box>}
                            <Box position="relative" sx={{ zIndex: 8000000 }}>
                                <TextField size='small' onBlur={e => submitScore( e, 'blur' )} onKeyUp={e => submitScore( e, 'keyup' )} onChange={e => setAssignedScore( e.target.value )} onFocus={e => e.target.select()} color="secondary" value={assignedScore || 0} InputProps={{ startAdornment: <Typography color="secondary" paddingRight="20px" variant='subtitle2'>SCORE:</Typography>, inputProps: { min: 0 } }} type="number" />
                            </Box>
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" overflow="auto" className="custom-scrollbar" flexGrow={1}>
                        <Box display="grid" gridTemplateColumns={{ md: "350px auto", xs: "1fr" }} flexGrow={1} overflow="auto" >
                            <Box display={{ md: "block", xs: "none" }} bgcolor={palette.contentBg} overflow="auto">
                                {AssignmentStore.getSubmission?.map( ( submission ) => (
                                    <ViewerSubmission selected={selectedSubmission} setSelectedSubmissionForAction={setSelectedSubmissionForAction} viewSubmission={viewSubmission} submission={submission} key={submission.assignment_submission_id} />
                                ) )}
                            </Box>
                            <Box position="relative" overflow="auto" flexShrink={0} height="100%" flexDirection="grid" gridTemplateRows="1fr" alignItems="center" justifyContent="center" flexGrow={1}>
                                {loadingSubmission ? <Box display="flex" justifyContent="center">
                                    <DataLoadingSpinner waitingMessage="Loading submission file..." />
                                </Box> : <DocumentViewer scale={pageWidthAndScale.scale} width={pageWidthAndScale.width} type={previewingFileName?.split( "." ).pop()} setFile={setPreviewFile} file={previewFile} />}
                                <Box sx={{ position: "fixed", bottom: "20px", clipPath: "inset(0px)", zIndex: 100 }} gap="10px" padding="0 20px" display="flex" justifyContent="space-between">
                                    <IconButton onClick={prevSubmission} sx={{ border: border[1], bgcolor: palette.common.bg + "66", color: palette.common.font, ":hover": { bgcolor: palette.common.bg + "99" } }}>
                                        {Icons.default.KeyBoardArrowLeft}
                                    </IconButton>
                                    <IconButton onClick={nextSubmission} sx={{ border: border[1], bgcolor: palette.common.bg + "66", color: palette.common.font, ":hover": { bgcolor: palette.common.bg + "99" } }}>
                                        {Icons.default.KeyBoardArrowRight}
                                    </IconButton>
                                </Box>
                            </Box>

                        </Box>

                    </Box>

                </Box>
            </Drawer>
            <AssignmentSubmissionDownloadDialog setPreviewFile={setPreviewFile} previewFile={previewFile} viewSubmission={viewSubmission} dialogType={dialogType} open={open} selectedValue={selectedSubmissionForAction} onClose={handleClose} />
            {selectedForDeletion && <ConfirmDialog actionName="Yes! delete it" status={Boolean( selectedForDeletion )} confirmAction={deleteSubmission} waiting={deleting} cancelAction={() => setSelectedForDeletion( null )} message={`Are you sure you want delete submission of ${selectedForDeletion.student_name}`} />}

            {/* Breadcrumb */}
            <BreadcrumbList items={[
                { label: "Assignments", link: "/faculty/assignment" },
                assignment_id,
                "Submissions"
            ]} />

            {/* Content */}
            <Paper sx={{ display: "flex", flexDirection: "column", overflow: "clip" }}>
                <Box
                    sx={{
                        background: `linear-gradient(322.12deg, #F3C400 -21.29%, #E25A26 124.22%)`
                    }}
                    padding="20px" display="flex" gap="20px" alignItems={{ sm: "center", xs: "flex-start" }} justifyContent="space-between" flexDirection={{ sm: "row", xs: "column" }}>
                    <Box color="white">
                        <Typography variant='h5'>Submissions</Typography>
                        <Typography variant="body2" >Manage assignment submissions.</Typography>
                    </Box>
                    {loaded && AssignmentStore.getSubmission.length > 0 && <Button onClick={() => exportAssignmentReport( AssignmentStore.getSubmission )} sx={{ background: "#BEFFC7", color: "black", '&:hover': { background: "#a6ffb2" } }} variant='contained' disableElevation startIcon={Icons.default.DownloadForOfflineIcon}>Export as Excel</Button>}
                </Box>

                {loaded && AssignmentStore.getSubmission.length > 0 && <Box overflow="auto" bgcolor={palette.contentBg} border={border[1]} flexGrow={1} borderRadius="5px" margin="20px"  >
                    <TableContainer sx={{ overflow: "auto", height: "100%" }} className="custom-scrollbar" >
                        <Table stickyHeader>
                            <TableHeader columns={columns} />
                            <TableBody>
                                {AssignmentStore.getSubmission?.map( submission => {
                                    return (
                                        <TableRow sx={{ bgcolor: palette.contentBg }} className='table-row' key={submission.assignment_submission_id}>
                                            <TableCell sx={{ fontSize: "12px", padding: columns[0].padding || "10px 20px", color: table.color }}>
                                                <strong>{submission.student_name}</strong>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "12px", padding: columns[1].padding || "10px 20px", color: table.color }}>
                                                <strong>{submission.assignment_submitted_by}</strong>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "12px", padding: columns[2].padding || "10px 20px", color: table.color }}>
                                                <strong>{formatDate( submission.assignment_submitted_on, { month: "short" } )}</strong>
                                            </TableCell>
                                            {/* <TableCell align='center' onClick={e => openDownloadDialog( e, submission )} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }}>
                                                <Button sx={{ fontSize: "12px", textTransform: "capitalize" }} size="small" color="primary" >View submissions</Button>
                                            </TableCell> */}
                                            <TableCell align="center" sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }}>
                                                <strong>{submission.assignment_marks || "Not Assigned"}</strong>
                                            </TableCell>
                                            {/* <TableCell align="center" sx={{ fontSize: "12px", padding: columns[4].padding || "10px 20px", color: table.color }}>
                                                <Typography maxWidth={columns[4].width} display="block" component="strong" fontSize="inherit" noWrap >{submission.assignment_remarks || "Not given"}</Typography>
                                            </TableCell> */}
                                            <TableCell sx={{
                                                fontSize: "12px",
                                                padding: columns[4].padding || "10px 20px",
                                                color: "secondary.main",
                                                background: "inherit",
                                            }}>
                                                <Box display="flex" justifyContent="flex-end" alignItems="center" gap="10px">
                                                    <Button onClick={e => openDownloadDialog( e, submission )} variant='outlined' sx={{ fontSize: "12px", width: "max-content", textTransform: "capitalize" }} size="small" color="primary" >View submissions</Button>
                                                    <Button sx={{ fontSize: "12px", width: "max-content", textTransform: "capitalize" }} onClick={e => openMarksAssignDialog( e, submission )} size="small" variant='outlined' color="success" >
                                                        Set Score
                                                    </Button>
                                                    {/* <Tooltip disableInteractive title="Add remarks" >
                                                        <IconButton onClick={e => openReMarksAssignDialog( e, submission )} size="small" color='secondary'>
                                                            <Comment sx={{ fontSize: "18px" }} />
                                                        </IconButton>
                                                    </Tooltip> */}
                                                    <Tooltip disableInteractive title="Delete this submission">
                                                        <IconButton onClick={e => setSelectedForDeletion( submission )} size="small" color="error" >{Icons.default.DeleteIconOutlined}</IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )
                                } )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>}

                {!loaded && <DataLoadingSpinner waitingMessage="Loading submissions..." />}
                {loaded && AssignmentStore.getSubmission.length === 0 && <Box padding="20px" flexGrow="1" margin="10px" borderRadius="5px" bgcolor={palette.contentBg} display="flex" flexDirection="column" alignItems="center" gap="10px" color="textSecondary" variant='h6' justifyContent="center" textAlign="center" >
                    <img src={noSubmissionImg} width="200px" alt="No submissions!" />
                    <Typography variant='subtitle2' marginTop="10px" color="textSecondary" >No submissions received yet!</Typography>
                </Box>}
            </Paper>
        </Box>
    )
} )

export default EvaluateAssignment
